import Banner from 'core-system/Banner'
import Loading from 'core-system/Loading'
import ProgramHeader from 'core-system/Program/ProgramHeader'
import ProgramMissingPaymentMethod from 'core-system/Program/ProgramMissingPaymentMethod'
import Text from 'core-system/Text'
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { getClientEnvironment } from 'redux/config/services/Constants'
import { AppState } from 'redux/config/store'
import { MicromobilityProgram } from 'redux/micromobility/micromobilityTypes'
import { ProgramGroup } from 'redux/programs/programsTypes'
import useQueryParam from 'shared/Hooks/useQueryParam'
import MicromobilityPreviewMap from './components/map/MicromobilityPreviewMap'
import MicromobilityPreviewModal from './components/MicromobilityPreviewModal'
import MicromobilityOverviewView from './MicromobilityOverviewView'
import NoPaymentModal from 'shared/components/NoPaymentModal'

const MicromobilityView = React.memo(() => {
  const navigate = useNavigate()
  const segmentId = useQueryParam('segmentId')

  const micromobilityProgramsLoaded = useSelector(
    (state: AppState) => state.micromobility.micromobilityProgramsLoaded
  )
  const micromobilityPrograms = useSelector(
    (state: AppState) => state.micromobility.micromobilityPrograms
  )
  const {
    profile,
    fileUploadHistory,
    segmentsMap,
    hasValidPaymentMethod,
    hasValidDistributionType,
  } = useSelector((state: AppState) => state.employer)

  const [programGroup, setProgramGroup] = useState<ProgramGroup>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isNoPaymentModalOpen, setIsNoPaymentModalOpen] = useState(false)

  const handleGetStartedPress = () => {
    if (hasValidPaymentMethod || !profile?.openRegistration) {
      navigate(`/micromobility/${segmentId}/activation`)
    } else {
      setIsNoPaymentModalOpen(true)
    }
  }

  useEffect(() => {
    setProgramGroup({
      active:
        micromobilityPrograms[segmentId] &&
        micromobilityPrograms[segmentId].active,
      nextMonth:
        micromobilityPrograms[segmentId] &&
        micromobilityPrograms[segmentId].nextMonth,
    })
  }, [micromobilityPrograms, segmentId])

  if (
    !micromobilityProgramsLoaded ||
    !profile ||
    !programGroup ||
    !fileUploadHistory
  ) {
    return <Loading fullPage />
  }

  if (
    micromobilityPrograms &&
    (!programGroup.active || programGroup.active.segment !== segmentId) &&
    (!programGroup.nextMonth || programGroup.nextMonth.segment !== segmentId)
  ) {
    return (
      <>
        <ProgramHeader segmentId={segmentId} />
        <Banner
          variant='cta'
          title='Start your Micromobility Program today!'
          description={`Give ${
            segmentsMap[segmentId].name === 'All Employees'
              ? 'All Commuters'
              : segmentsMap[segmentId].name
          } safe, sustainable, and fun commuting options.`}
          cta='Get Started'
          ctaCallback={() => handleGetStartedPress()}
        />
        <MicromobilityPreviewModal
          open={isModalOpen}
          closeModal={() => setIsModalOpen(false)}
          segmentId={segmentId}
        />
        <NoPaymentModal
          open={isNoPaymentModalOpen}
          closeModal={() => setIsNoPaymentModalOpen(false)}
          product='micromobility'
        />
        <Text variant='h3' margin='0.5rem 0 1rem'>
          Potential Micromobility Engagement
        </Text>
        <MicromobilityPreviewMap />
      </>
    )
  }

  const isValid = hasValidDistributionType && hasValidPaymentMethod
  const isNextMonth = !programGroup.active && programGroup.nextMonth
  const isCancelling =
    !programGroup.active &&
    programGroup.nextMonth &&
    programGroup.nextMonth.status === 'CANCELLING'
  const isPending = isNextMonth && !isCancelling
  const isDevCheck =
    getClientEnvironment() === 'development' && !isPending && !isCancelling
  const isOverviewCheck = programGroup.active && isValid

  return (
    <>
      <ProgramHeader
        segmentId={segmentId}
        manageRoute={
          isDevCheck || isOverviewCheck
            ? `/micromobility/${segmentId}/manage`
            : null
        }
      />
      {isPending && (
        <Banner
          variant='cta'
          background='bannerGeneral'
          title='Starting Soon!'
          description={`Your Micromobility Program is scheduled to begin on ${moment(
            programGroup.nextMonth.startDate
          ).format('MMMM Do, YYYY')}.`}
          cta='Manage Program'
          ctaCallback={() => navigate(`/micromobility/${segmentId}/manage`)}
        />
      )}
      {isCancelling && (
        <Banner
          variant='cta'
          background='bannerGeneral'
          title='Program cancelling'
          description='Your program cancellation request has been submitted. Our team will reach out soon.'
          cta='Manage Program'
          ctaCallback={() => navigate(`/micromobility/${segmentId}/manage`)}
        />
      )}
      {isDevCheck ? (
        <MicromobilityOverviewView
          activeProgram={programGroup.active as MicromobilityProgram}
        />
      ) : (
        <>
          {!isValid && (
            <ProgramMissingPaymentMethod from={`/micromobility/${segmentId}`} />
          )}
          {isOverviewCheck && (
            <MicromobilityOverviewView
              activeProgram={programGroup.active as MicromobilityProgram}
            />
          )}
        </>
      )}
      {isNextMonth && (
        <>
          <Text variant='h3' margin='0.5rem 0 1rem'>
            Potential Micromobility Engagement
          </Text>
          <MicromobilityPreviewMap />
        </>
      )}
    </>
  )
})

// Helps to identify component in React error logs
if (process.env.NODE_ENV !== 'production') {
  MicromobilityView.displayName = 'MicromobilityView'
}

export default MicromobilityView
